import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField,
} from '@mui/material';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import DecimalField from '../shared/DecimalField';
import VendorAutocomplete from './VendorAutocomplete';
import { KINDS, PL_API, REFERENCE_TYPES, purchaseErrorMessage } from './purchaseUtils';

const EMPTY = {
  kind: 'invoice',
  vendor: null,
  vendor_name_on_document: '',
  invoice_number: '',
  invoice_date: '',
  due_date: '',
  total_net: '',
  total_vat: '',
  total_gross: '',
  reference_number: '',
  reference_type: 'fi',
  payee_iban: '',
  cost_category: '',
  description: '',
};

/**
 * "Syötä käsin": a purchase invoice without a document. Amounts are entered as printed on
 * the paper (net, VAT, total), so there is no VAT entry-mode toggle here.
 */
export default function ManualInvoiceDialog({ open, onClose, onCreated, categories }) {
  const { t } = useTranslation();
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (open) {
      setForm(EMPTY);
      setError('');
    }
  }, [open]);

  const set = (field) => (value) => setForm((prev) => ({ ...prev, [field]: value }));
  const setText = (field) => (e) => set(field)(e.target.value);

  const valid = form.total_gross !== '' && (form.vendor || form.vendor_name_on_document.trim()) && form.invoice_number.trim();

  const handleSave = async () => {
    setSaving(true);
    setError('');
    const payload = {
      kind: form.kind,
      vendor: form.vendor?.id || null,
      vendor_name_on_document: form.vendor_name_on_document || form.vendor?.name || '',
      invoice_number: form.invoice_number.trim(),
      invoice_date: form.invoice_date || null,
      due_date: form.due_date || null,
      total_net: form.total_net === '' ? null : form.total_net,
      total_vat: form.total_vat === '' ? null : form.total_vat,
      total_gross: form.total_gross,
      reference_number: form.reference_number.replace(/\s+/g, ''),
      reference_type: form.reference_number ? form.reference_type : 'none',
      payee_iban: form.payee_iban.replace(/\s+/g, '').toUpperCase(),
      cost_category: form.cost_category || null,
      description: form.description,
    };
    if (payload.total_net === null && payload.total_vat === null) {
      delete payload.total_net;
      delete payload.total_vat;
    }
    try {
      const response = await axios.post(`${PL_API}/invoices/`, payload);
      onCreated?.(response.data);
      onClose();
    } catch (err) {
      setError(purchaseErrorMessage(t, err, 'purchaseLedger.manual.failed'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={saving ? undefined : onClose} maxWidth="sm" fullWidth data-testid="pl-manual-dialog">
      <DialogTitle>{t('purchaseLedger.manual.title')}</DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
          <FormControl size="small" fullWidth>
            <InputLabel>{t('purchaseLedger.fields.kind')}</InputLabel>
            <Select value={form.kind} label={t('purchaseLedger.fields.kind')} onChange={setText('kind')} data-testid="pl-manual-kind">
              {KINDS.map((k) => (
                <MenuItem key={k} value={k}>
                  {t(`purchaseLedger.kind.${k}`)}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <VendorAutocomplete
            value={form.vendor}
            onChange={(vendor) => setForm((prev) => ({ ...prev, vendor, vendor_name_on_document: vendor?.name || prev.vendor_name_on_document }))}
            label={t('purchaseLedger.fields.vendor')}
            testId="pl-manual-vendor"
          />
          {!form.vendor && (
            <TextField
              size="small"
              label={t('purchaseLedger.fields.vendorNameOnDocument')}
              value={form.vendor_name_on_document}
              onChange={setText('vendor_name_on_document')}
              inputProps={{ 'data-testid': 'pl-manual-vendor-name' }}
            />
          )}
          <TextField
            size="small"
            label={t('purchaseLedger.fields.invoiceNumber')}
            value={form.invoice_number}
            onChange={setText('invoice_number')}
            required
            inputProps={{ 'data-testid': 'pl-manual-invoice-number' }}
          />
          <Box sx={{ display: 'flex', gap: 2, flexDirection: { xs: 'column', sm: 'row' } }}>
            <TextField
              size="small"
              type="date"
              label={t('purchaseLedger.fields.invoiceDate')}
              value={form.invoice_date}
              onChange={setText('invoice_date')}
              InputLabelProps={{ shrink: true }}
              fullWidth
              inputProps={{ 'data-testid': 'pl-manual-invoice-date' }}
            />
            <TextField
              size="small"
              type="date"
              label={t('purchaseLedger.fields.dueDate')}
              value={form.due_date}
              onChange={setText('due_date')}
              InputLabelProps={{ shrink: true }}
              fullWidth
              disabled={form.kind === 'receipt'}
              inputProps={{ 'data-testid': 'pl-manual-due-date' }}
            />
          </Box>
          <Box sx={{ display: 'flex', gap: 2, flexDirection: { xs: 'column', sm: 'row' } }}>
            <DecimalField size="small" label={t('purchaseLedger.fields.totalNet')} value={form.total_net} onChange={set('total_net')} fullWidth allowNegative />
            <DecimalField size="small" label={t('purchaseLedger.fields.totalVat')} value={form.total_vat} onChange={set('total_vat')} fullWidth allowNegative />
            <DecimalField
              size="small"
              label={t('purchaseLedger.fields.totalGross')}
              value={form.total_gross}
              onChange={set('total_gross')}
              fullWidth
              required
              allowNegative
              inputProps={{ 'data-testid': 'pl-manual-total-gross' }}
            />
          </Box>
          <Box sx={{ display: 'flex', gap: 2, flexDirection: { xs: 'column', sm: 'row' } }}>
            <TextField
              size="small"
              label={t('purchaseLedger.fields.reference')}
              value={form.reference_number}
              onChange={setText('reference_number')}
              fullWidth
            />
            <FormControl size="small" sx={{ minWidth: 140 }}>
              <InputLabel>{t('purchaseLedger.fields.referenceType')}</InputLabel>
              <Select value={form.reference_type} label={t('purchaseLedger.fields.referenceType')} onChange={setText('reference_type')}>
                {REFERENCE_TYPES.map((r) => (
                  <MenuItem key={r} value={r}>
                    {t(`purchaseLedger.referenceType.${r}`)}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
          <TextField size="small" label="IBAN" value={form.payee_iban} onChange={setText('payee_iban')} />
          <FormControl size="small" fullWidth>
            <InputLabel>{t('purchaseLedger.fields.costCategory')}</InputLabel>
            <Select
              value={form.cost_category}
              label={t('purchaseLedger.fields.costCategory')}
              onChange={setText('cost_category')}
              data-testid="pl-manual-category"
            >
              <MenuItem value="">
                <em>{t('purchaseLedger.fields.noCategory')}</em>
              </MenuItem>
              {(categories || []).map((c) => (
                <MenuItem key={c.id} value={c.id}>
                  {c.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField size="small" label={t('purchaseLedger.fields.description')} value={form.description} onChange={setText('description')} />
          {error && <Alert severity="error">{error}</Alert>}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>
          {t('Cancel')}
        </Button>
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={saving || !valid}
          startIcon={saving ? <CircularProgress size={16} /> : null}
          data-testid="pl-manual-save"
        >
          {t('Save')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
